import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export default function SubmitUpdateDialog({ jobId, open, onOpenChange, initialStage, stages }) {
  const qc = useQueryClient();
  const options = useMemo(() => (stages && stages.length ? stages : STAGES), [stages]);
  const [form, setForm] = useState({ stage: options[0], proposed_status: "in_progress", notes: "", metrics: "", custom_label: "" });

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, stage: initialStage || options[0] }));
  }, [open, initialStage, options]);

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.post(`/jobs/${jobId}/stage-updates`, payload)).data,
    onSuccess: () => {
      toast.success("Update submitted — Pending Review");
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
      onOpenChange(false);
      setForm({ stage: STAGES[0], proposed_status: "in_progress", notes: "", metrics: "", custom_label: "" });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="submit-update-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Submit Stage Update</DialogTitle>
          <DialogDescription>Your update will be marked Pending Review until a Manager or Owner finalises it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form); }} className="space-y-4">
          <div>
            <Label>Stage</Label>
            <Select value={form.stage} onValueChange={(v) => setForm((f) => ({ ...f, stage: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="update-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((s) => <SelectItem key={s} value={s} data-testid={`update-stage-option-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.stage === "Custom Work" && (
            <div>
              <Label>Custom task title</Label>
              <Input value={form.custom_label} onChange={(e) => setForm((f) => ({ ...f, custom_label: e.target.value }))}
                placeholder="e.g. Special coating" className="mt-1.5" data-testid="update-custom-label-input" />
            </div>
          )}
          <div>
            <Label>Status</Label>
            <Select value={form.proposed_status} onValueChange={(v) => setForm((f) => ({ ...f, proposed_status: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="update-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_progress" data-testid="update-status-in-progress">In Progress</SelectItem>
                <SelectItem value="completed" data-testid="update-status-completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3} placeholder="Operational notes..." className="mt-1.5" data-testid="update-notes-input" />
          </div>
          <div>
            <Label>Metrics</Label>
            <Input value={form.metrics} onChange={(e) => setForm((f) => ({ ...f, metrics: e.target.value }))}
              placeholder="e.g. 42 units / 6 hrs" className="mt-1.5" data-testid="update-metrics-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="submit-update-confirm-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit for Review
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
